import React, { useState, useEffect } from 'react';
import {
  Box, Modal, Typography, IconButton, Button, List, ListItem, ListItemText, Select, MenuItem
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import ModalFolderPermissions from './ModalFolderPermissions';

interface User {
  name: string;
  surname: string;
  company: string;
  email: string;
  userId: string;
  language: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  users: { user: User; userStatus: string; userAccessType: string }[];
  onCreate: () => void;
  onPermissionChange: (index: number, newAccessType: string) => void;
  onUserStatus: (index: number) => void;
  onDelete: (user: User) => void;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'ACTIVE':
      return 'green';
    case 'INACTIVE':
      return 'goldenrod';
    case 'SUSPENDED':
      return 'red';
    default:
      return 'grey'; // fallback
  }
};


const ModalUsers: React.FC<Props> = ({ open, onClose, users, onCreate, onPermissionChange, onUserStatus, onDelete }) => {

  const { t, i18n } = useTranslation();
  const [showFolderPermissions, setShowFolderPermissions] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
    
  useEffect(() => {
    const storedLang = localStorage.getItem('language') || 'EN';
    i18n.changeLanguage(storedLang);
  }, []);

  const onFolderPermissions = (user: User) => {
    setSelectedUser(user);
    setShowFolderPermissions(true);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{
        position: 'absolute',
        top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 600, bgcolor: 'background.paper',
        borderRadius: 2, boxShadow: 24, p: 3,
        maxHeight: '70vh',
        display: 'flex', 
        flexDirection: 'column',
      }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <PersonIcon sx={{ marginRight: 1, color: '#73AF73' }} />
            <Typography variant="h6" sx={{ color: '#73AF73' }}>{t('users')}</Typography>
          </Box>

          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={onCreate}
            sx={{ marginRight: 1, backgroundColor: '#73AF73' }}
          >
            {t('newUser')}
          </Button>

          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* User List */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          <List>
            {users.map(({ user, userStatus, userAccessType }, idx) => (
              <ListItem key={idx} divider sx={{ display: 'flex', alignItems: 'center' }}>
                <ListItemText
                  primary={`${user.name} ${user.surname}`}
                  secondary={`${user.company} | ${user.email}`}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {/* Permissions ComboBox */}
                  <Select
                    size="small"
                    value={userAccessType}
                    onChange={(e) => onPermissionChange(idx, e.target.value)}
                    sx={{ minWidth: 120 }}
                    disabled={userAccessType === 'LOADING...'}
                  >
                    <MenuItem value="NOT_ASSIGNED">NOT_ASSIGNED</MenuItem>
                    <MenuItem value="ADMIN">ADMIN</MenuItem>
                    <MenuItem value="MEMBER">MEMBER</MenuItem>
                  </Select>

                  <IconButton onClick={() => onUserStatus(idx)}>
                    <CheckCircleIcon sx={{ color: getStatusColor(userStatus) }} />
                  </IconButton>

                  <IconButton onClick={() => onFolderPermissions(user)}>
                    <SecurityIcon />
                  </IconButton>

                  <IconButton onClick={() => onDelete(user)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </ListItem>
            ))}
          </List>
        </Box>
        {selectedUser && (
          <ModalFolderPermissions
            open={showFolderPermissions}
            onClose={() => setShowFolderPermissions(false)}
            user={selectedUser}
          />
        )}
      </Box>
    </Modal>
  );
};

export default ModalUsers;